import React from 'react';
import Marca from '../../components/Marca';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { LogOut, IdCard, MapPin, Fuel, Car, Route, AlertTriangle, Receipt, Plus } from 'lucide-react';
import { portalService } from '../../services/portal';
import { authService } from '../../services/auth';
import { formatDate, formatKilometers } from '../../utils/formatters';
import { Veiculo } from '../../types';
import ViagensDoMotorista from './ViagensDoMotorista';

const cnhInfo = (vencimento?: string | null) => {
  if (!vencimento) return { label: 'Sem data', cls: 'bg-gray-100 text-gray-700', alerta: false };
  const dias = Math.ceil((new Date(vencimento).getTime() - Date.now()) / 86_400_000);
  if (dias < 0) return { label: 'CNH vencida', cls: 'bg-red-100 text-red-800', alerta: true };
  if (dias <= 30) return { label: `Vence em ${dias} dia(s)`, cls: 'bg-yellow-100 text-yellow-800', alerta: true };
  return { label: 'CNH em dia', cls: 'bg-green-100 text-green-800', alerta: false };
};

const Stat: React.FC<{ icon: React.ElementType; label: string; value: React.ReactNode; color: string }> = ({ icon: Icon, label, value, color }) => (
  <div className="bg-white rounded-xl shadow-sm border p-4 flex items-center gap-3">
    <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${color}1a` }}>
      <Icon className="w-5 h-5" style={{ color }} />
    </div>
    <div className="min-w-0">
      <p className="text-xs text-gray-500">{label}</p>
      <p className="text-base font-bold text-gray-900 truncate">{value}</p>
    </div>
  </div>
);

const PortalMotorista: React.FC = () => {
  const navigate = useNavigate();

  const { data, isLoading, error } = useQuery({
    queryKey: ['portal-me'],
    queryFn: () => portalService.getMe(),
  });

  const handleLogout = async () => {
    await authService.logout();
    navigate('/login');
  };

  return (
    <div className="min-h-screen bg-secondary-50">
      {/* Barra superior */}
      <header className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-3xl mx-auto px-4 h-14 flex items-center justify-between">
          <div className="flex items-center gap-2">
            {/* Simbolo: no cabecalho do celular a marca completa nao caberia
                em tamanho legivel, e o titulo ao lado ja diz onde ele esta. */}
            <Marca versao="simbolo" tamanho={32} />
            <span className="font-semibold text-secondary-900">Portal do Motorista</span>
          </div>
          <button onClick={handleLogout} className="inline-flex items-center gap-1.5 text-sm text-secondary-600 hover:text-red-600">
            <LogOut className="w-4 h-4" /> Sair
          </button>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-6 space-y-5">
        {isLoading && (
          <div className="flex justify-center items-center h-40">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary-600" />
          </div>
        )}

        {(error || (!isLoading && !data?.data)) && (
          <div className="bg-white rounded-xl border p-6 text-center text-gray-500">
            Não foi possível carregar seus dados. Verifique se seu cadastro foi vinculado a um motorista.
          </div>
        )}

        {data?.data && (() => {
          const m = data.data;
          const est = (data.estatisticas || {}) as any;
          const cnh = cnhInfo(m.vencimento_cnh);
          const veiculos: Veiculo[] = m.veiculos ?? [];

          return (
            <>
              {/* Saudação */}
              <div>
                <p className="text-sm text-secondary-500">Olá,</p>
                <h1 className="text-2xl font-bold text-secondary-900">{m.nome.split(' ')[0]} 👋</h1>
              </div>

              {/* Alerta de CNH */}
              {cnh.alerta && (
                <div className="flex items-center gap-3 bg-yellow-50 border border-yellow-200 rounded-xl p-4">
                  <AlertTriangle className="w-5 h-5 text-yellow-600 flex-shrink-0" />
                  <div>
                    <p className="text-sm font-semibold text-yellow-800">Atenção com sua CNH</p>
                    <p className="text-xs text-yellow-700">Sua habilitação {cnh.label.toLowerCase()} — vencimento em {formatDate(m.vencimento_cnh)}.</p>
                  </div>
                </div>
              )}

              {/* Lançar
                  Fica logo abaixo da saudação porque é o que traz o motorista
                  ao portal: ele abre o app com o cupom na mão. */}
              <div className="grid grid-cols-2 gap-3">
                <button
                  type="button"
                  onClick={() => navigate('/motorista/lancar/abastecimento')}
                  className="flex flex-col items-center justify-center gap-1 rounded-xl bg-primary-600 py-5 text-white shadow-sm hover:bg-primary-700"
                >
                  <span className="relative"><Fuel className="w-6 h-6" /><Plus className="w-3 h-3 absolute -right-1 -top-1" /></span>
                  <span className="text-sm font-semibold">Abastecimento</span>
                </button>
                <button
                  type="button"
                  onClick={() => navigate('/motorista/lancar/despesa')}
                  className="flex flex-col items-center justify-center gap-1 rounded-xl bg-white border border-secondary-200 py-5 text-secondary-700 shadow-sm hover:bg-secondary-50"
                >
                  <span className="relative"><Receipt className="w-6 h-6" /><Plus className="w-3 h-3 absolute -right-1 -top-1" /></span>
                  <span className="text-sm font-semibold">Despesa</span>
                </button>
              </div>

              {/* Estatísticas */}
              <div className="grid grid-cols-2 gap-3">
                <Stat icon={Route} label="Viagens" value={est.total_viagens ?? 0} color="#0891b2" />
                <Stat icon={MapPin} label="Km rodados" value={formatKilometers(est.km_total_rodados || 0)} color="#2563eb" />
                <Stat icon={Fuel} label="Abastecimentos" value={est.total_abastecimentos ?? 0} color="#d97706" />
                <Stat icon={IdCard} label="CNH" value={<span className={`inline-flex px-2 py-0.5 text-xs font-semibold rounded-full ${cnh.cls}`}>{cnh.label}</span>} color="#059669" />
              </div>

              {/* Meus veículos */}
              <section className="bg-white rounded-xl shadow-sm border p-5">
                <h2 className="text-base font-semibold text-secondary-900 mb-3 flex items-center gap-2"><Car className="w-5 h-5 text-secondary-400" /> Meus veículos</h2>
                {veiculos.length === 0 ? (
                  <p className="text-sm text-secondary-400">Nenhum veículo vinculado a você.</p>
                ) : (
                  <div className="space-y-2">
                    {veiculos.map((v) => (
                      <div key={v.id} className="flex items-center justify-between p-2.5 rounded-lg bg-secondary-50">
                        <div>
                          <p className="text-sm font-semibold text-secondary-900">{v.placa}</p>
                          <p className="text-xs text-secondary-500">{v.marca} {v.modelo}</p>
                        </div>
                        <span className="text-xs text-secondary-400">{v.status}</span>
                      </div>
                    ))}
                  </div>
                )}
              </section>

              {/* Minhas viagens — e onde ele informa a quilometragem, que so
                  quem esta com o carro consegue ler. */}
              <ViagensDoMotorista />

              <p className="text-center text-xs text-secondary-400 pt-2">Portal do Motorista · FROVOX</p>
            </>
          );
        })()}
      </main>
    </div>
  );
};

export default PortalMotorista;
